import { useAppSelector } from "app/hooks";
import { useCallback, useEffect, useState } from "react";
import { selectTableListings } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { ListingActions, ListingState } from "types/listing";

let initialListing: ListingState = {
  order: undefined,
  page: 1,
  property: "",
  rowsPerPage: 10,
  rowsSelected: new Set(),
};

function getListing(
  listing: ListingState,
  action: { type: ListingActions; payload: any; ids?: any; id?: any },
): ListingState {
  if (!action.type) {
    throw new Error();
  }
  switch (action.type) {
    case ListingActions.CHANGE_PAGE:
      listing.rowsSelected.clear();
      return {
        ...listing,
        page: action.payload,
      };
    case ListingActions.CHANGE_ROWS_PER_PAGE:
      listing.rowsSelected.clear();
      return {
        ...listing,
        page: 1,
        rowsPerPage: action.payload,
      };
    case ListingActions.UNSELECT_ALL:
      listing.rowsSelected.clear();
      return {
        ...listing,
      };
    case ListingActions.SELECT_ALL:
      return {
        ...listing,
        rowsSelected: action.ids,
      };
    case ListingActions.SELECT_ROW:
      return {
        ...listing,
        rowsSelected: listing.rowsSelected.add(action.id),
      };
    case ListingActions.UNSELECT_ROW:
      listing.rowsSelected.delete(action.id);
      return {
        ...listing,
      };
    case ListingActions.SET_SORT:
      listing.rowsSelected.clear();
      action.payload.onSortCustomAction?.(
        action.payload.order,
        action.payload.property,
        action.payload.rowsPerPage,
        action.payload.page,
      );
      return {
        ...listing,
        page: action.payload.page,
        order: action.payload.order,
        property: action.payload.property,
      };
  }
}
export function useListing(tableName?: string) {
  const [listing, setListing] = useState(
    useAppSelector((state) => selectTableListings(state, tableName || "")),
  );

  const currentListing = useAppSelector((state) =>
    selectTableListings(state, tableName || ""),
  );

  useEffect(() => {
    setListing({
      ...initialListing,
      order: currentListing?.order || undefined,
      property: currentListing?.property || "",
      rowsPerPage: currentListing?.rowsPerPage || 10,
      page: currentListing?.page || 1,
    });
  }, [currentListing]);
  const { setPersistentListingSorting } = useTablesStateHelpers();

  const updateListing = useCallback(
    ({ payload, type }: { payload: any; type: ListingActions }) => {
      const updatedListing = getListing(
        {
          ...initialListing,
          order: listing?.order || undefined,
          property: listing?.property || "",
          rowsPerPage: listing?.rowsPerPage || 10,
          page: listing?.page || 1,
        },
        { type, payload },
      );

      if (tableName) {
        return setPersistentListingSorting(
          updatedListing.order,
          updatedListing.property,
          updatedListing.rowsPerPage,
          updatedListing.page,
          tableName,
        );
      }

      setListing(updatedListing);
    },
    [listing, tableName, setPersistentListingSorting],
  );

  return {
    listing: {
      ...initialListing,
      order: listing?.order || undefined,
      property: listing?.property || "",
      rowsPerPage: listing?.rowsPerPage || 10,
      page: listing?.page || 1,
    },
    updateListing,
  };
}
